"use client";

import { c as _c } from "react/compiler-runtime";
import { IconRadio } from "@central-icons-react/square-outlined-radius-0-stroke-1.5";
import { IconCheckOutline18, IconCopyOutline18, IconPaperPlane2Outline18 } from "nucleo-ui-outline-18";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useCopyToClipboard } from "@/hooks/useCopyToClipboard";
import { useTriggerTest } from "@/hooks/useTriggerTest";
import { useWorkflowWebhookSecret } from "@/hooks/useWorkflowWebhookSecret";
import { toast } from "@/lib/toast";
import { generatePerWorkflowWebhookUrl } from "@/utils/webhookUtils";
import { PixelLoader } from "@/components/ui/pixel-loader";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import JsonEditorBase from "../../form-fields/JsonEditorBase";
import { generateSampleFromInputSchema } from "../sampleGenerators";
import { SchemaDrivenForm } from "./SchemaDrivenForm";
import type { TriggerArchetypeProps } from "./types";
import { usePollingListener } from "./usePollingListener";
const DEFAULT_TIMEOUT_MS = 30_000;

/**
 * LiveListener archetype — for manual triggers, shows the webhook URL + JSON editor
 * with a "Send Webhook" button. For other triggers, polls for real events.
 */
export function LiveListener(t0) {
  const $ = _c(13);
  const {
    triggerType,
    triggerPayload,
    onRun,
    isRunning,
    supportsLiveTest,
    workflowId,
    inputSchema,
    onRegisterPrimaryAction
  } = t0;
  const isManualTrigger = triggerType === "manual";
  if (isManualTrigger) {
    let t1;
    if ($[0] !== inputSchema || $[1] !== isRunning || $[2] !== onRun || $[3] !== triggerType || $[4] !== workflowId) {
      t1 = <WebhookLiveRunner workflowId={workflowId} onRun={onRun} isRunning={isRunning} inputSchema={inputSchema} triggerType={triggerType} />;
      $[0] = inputSchema;
      $[1] = isRunning;
      $[2] = onRun;
      $[3] = triggerType;
      $[4] = workflowId;
      $[5] = t1;
    } else {
      t1 = $[5];
    }
    return t1;
  }
  let t1;
  if ($[6] !== isRunning || $[7] !== onRegisterPrimaryAction || $[8] !== onRun || $[9] !== supportsLiveTest || $[10] !== triggerPayload || $[11] !== triggerType) {
    t1 = <GenericLiveListener triggerType={triggerType} triggerPayload={triggerPayload} onRun={onRun} isRunning={isRunning} supportsLiveTest={supportsLiveTest} onRegisterPrimaryAction={onRegisterPrimaryAction} />;
    $[6] = isRunning;
    $[7] = onRegisterPrimaryAction;
    $[8] = onRun;
    $[9] = supportsLiveTest;
    $[10] = triggerPayload;
    $[11] = triggerType;
    $[12] = t1;
  } else {
    t1 = $[12];
  }
  return t1;
}

// =============================================================================
// Webhook-specific live runner
// =============================================================================

function WebhookLiveRunner({
  workflowId,
  onRun,
  isRunning,
  inputSchema,
  triggerType
}: {
  workflowId?: string;
  onRun: TriggerArchetypeProps["onRun"];
  isRunning: boolean;
  inputSchema?: TriggerArchetypeProps["inputSchema"];
  triggerType: string;
}) {
  const {
    secret,
    isLoading: isLoadingSecret
  } = useWorkflowWebhookSecret(workflowId);
  const {
    isCopied,
    copyToClipboard
  } = useCopyToClipboard();
  const defaultJsonBody = useMemo(() => {
    const sampleBody = inputSchema && inputSchema.length > 0 ? generateSampleFromInputSchema(inputSchema) : {
      message: "Hello from B3OS"
    };
    return JSON.stringify(sampleBody, null, 2);
  }, [inputSchema]);
  const [activeTab, setActiveTab] = useState<"manual" | "webhook">("manual");
  const [jsonBody, setJsonBody] = useState(defaultJsonBody);
  const [isSending, setIsSending] = useState(false);
  const webhookUrl = workflowId && secret ? generatePerWorkflowWebhookUrl(workflowId, secret) : null;
  useEffect(() => {
    setJsonBody(defaultJsonBody);
  }, [defaultJsonBody]);
  useEffect(() => {
    if (activeTab === "webhook") {
      onRun(null);
    }
  }, [activeTab, onRun]);
  const handleSendWebhook = useCallback(async () => {
    if (!webhookUrl) return;
    let parsedBody: Record<string, unknown>;
    try {
      parsedBody = JSON.parse(jsonBody);
    } catch {
      toast.error("Invalid JSON body");
      return;
    }
    setIsSending(true);
    try {
      const res = await fetch(webhookUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          data: parsedBody
        })
      });
      if (!res.ok) {
        const text = await res.text().catch(() => "");
        throw new Error(text || `HTTP ${res.status}`);
      }
      toast.success("Webhook sent — workflow triggered");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to send webhook");
    } finally {
      setIsSending(false);
    }
  }, [webhookUrl, jsonBody]);
  if (isLoadingSecret) {
    return <div className="flex items-center gap-2 py-4">
        <PixelLoader size="sm" color="blue" />
        <span className="label-xs">Loading webhook URL...</span>
      </div>;
  }
  return <Tabs value={activeTab} onValueChange={value => setActiveTab(value as "manual" | "webhook")}>
      <div className="space-y-4">
        <div>
          <TabsList>
            <TabsTrigger value="manual">Run manually</TabsTrigger>
            <TabsTrigger value="webhook">Trigger with webhook</TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="manual" className="mt-0">
          <SchemaDrivenForm triggerType={triggerType} triggerName={undefined} triggerPayload={{}} resultSchema={undefined} inputSchema={inputSchema} workflowId={undefined} supportsLiveTest={false} propValues={undefined} onRun={onRun} isRunning={isRunning} isPublished connectorId={undefined} />
        </TabsContent>

        <TabsContent value="webhook" className="mt-0 min-w-0 space-y-4">
          {webhookUrl && <div className="min-w-0 space-y-1.5">
              <span className="field-label">Webhook URL</span>
              <div className="border-ds-border-default dark:bg-ds-neutral-800 flex items-center gap-2 rounded-lg border bg-zinc-50 px-3 py-2.5">
                <div className="scrollbar-none w-0 flex-1 overflow-x-auto">
                  <code className="mono-xs whitespace-nowrap">{webhookUrl}</code>
                </div>
                <button type="button" onClick={() => copyToClipboard(webhookUrl)} className="text-ds-text-disabled hover:text-ds-text-subtle flex-shrink-0 rounded p-1 transition-colors dark:hover:text-zinc-300">
                  {isCopied ? <IconCheckOutline18 className="h-3.5 w-3.5 text-green-600 dark:text-green-400" /> : <IconCopyOutline18 className="h-3.5 w-3.5" />}
                </button>
              </div>
              <p className="caption-xs">
                POST to this URL with a JSON body. Access fields via{" "}
                <code className="bg-ds-neutral-100 dark:bg-ds-neutral-800 rounded px-1">
                  {"{{root.result.body.fieldName}}"}
                </code>
              </p>
            </div>}

          <JsonEditorBase label="Request Body" value={jsonBody} onChange={setJsonBody} placeholder='{ "message": "Hello" }' minHeight="120px" />

          <Button onClick={handleSendWebhook} disabled={isRunning || isSending || !webhookUrl} className="w-full">
            {isSending ? <PixelLoader size="sm" /> : <IconPaperPlane2Outline18 className="h-4 w-4" />}
            {isSending ? "Sending..." : "Send Webhook"}
          </Button>
        </TabsContent>
      </div>
    </Tabs>;
}

// =============================================================================
// Generic live listener (non-webhook triggers)
// =============================================================================

function GenericLiveListener(t0) {
  const $ = _c(41);
  const {
    triggerType,
    triggerPayload,
    onRun,
    isRunning,
    supportsLiveTest,
    onRegisterPrimaryAction
  } = t0;
  const {
    fetchLiveData
  } = useTriggerTest();
  const detectedPayloadRef = useRef(null);
  let t1;
  if ($[0] !== fetchLiveData || $[1] !== triggerPayload || $[2] !== triggerType) {
    t1 = async () => {
      const response = await fetchLiveData(triggerType, triggerPayload);
      if (!response?.success) {
        return false;
      }
      const payload = response.results?.[0] ?? response.result;
      if (payload && Object.keys(payload).length > 0) {
        detectedPayloadRef.current = payload;
        return true;
      }
      return false;
    };
    $[0] = fetchLiveData;
    $[1] = triggerPayload;
    $[2] = triggerType;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const onPoll = t1;
  let t2;
  if ($[4] !== onRun) {
    t2 = () => {
      if (detectedPayloadRef.current) {
        onRun(detectedPayloadRef.current);
      }
    };
    $[4] = onRun;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const onDetected = t2;
  let t3;
  if ($[6] !== onDetected || $[7] !== onPoll) {
    t3 = {
      timeoutMs: DEFAULT_TIMEOUT_MS,
      timeoutMessage: "Timed out waiting for an event. Try triggering the event and listening again.",
      onPoll,
      onDetected
    };
    $[6] = onDetected;
    $[7] = onPoll;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const {
    isListening,
    secondsRemaining,
    error,
    startListening: hookStart,
    stopListening
  } = usePollingListener(t3);
  const [liveTestError, setLiveTestError] = useState(null);
  let t4;
  if ($[9] !== hookStart || $[10] !== supportsLiveTest) {
    t4 = () => {
      if (!supportsLiveTest) {
        setLiveTestError("Live testing is not supported for this trigger type.");
        return;
      }
      setLiveTestError(null);
      detectedPayloadRef.current = null;
      hookStart();
    };
    $[9] = hookStart;
    $[10] = supportsLiveTest;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  const startListening = t4;
  const displayError = liveTestError ?? error;
  let t5;
  let t6;
  if ($[12] !== isListening || $[13] !== isRunning || $[14] !== onRegisterPrimaryAction || $[15] !== startListening || $[16] !== stopListening) {
    t5 = () => {
      if (!onRegisterPrimaryAction) {
        return;
      }
      onRegisterPrimaryAction(isListening ? {
        label: "Stop Listening",
        onClick: stopListening,
        leadingIcon: <IconRadio className="h-3.5 w-3.5" />
      } : {
        label: "Start Listening",
        onClick: startListening,
        disabled: isRunning,
        leadingIcon: <IconRadio className="h-3.5 w-3.5" />
      });
    };
    t6 = [isListening, isRunning, onRegisterPrimaryAction, startListening, stopListening];
    $[12] = isListening;
    $[13] = isRunning;
    $[14] = onRegisterPrimaryAction;
    $[15] = startListening;
    $[16] = stopListening;
    $[17] = t5;
    $[18] = t6;
  } else {
    t5 = $[17];
    t6 = $[18];
  }
  useEffect(t5, t6);
  let t7;
  let t8;
  if ($[19] !== onRegisterPrimaryAction) {
    t7 = () => () => onRegisterPrimaryAction?.(null);
    t8 = [onRegisterPrimaryAction];
    $[19] = onRegisterPrimaryAction;
    $[20] = t7;
    $[21] = t8;
  } else {
    t7 = $[20];
    t8 = $[21];
  }
  useEffect(t7, t8);
  const t9 = `absolute inline-flex h-full w-full animate-ping rounded-full opacity-75 ${isListening ? "bg-green-500" : "bg-zinc-400 dark:bg-zinc-500"}`;
  let t10;
  if ($[22] !== t9) {
    t10 = <span className={t9} />;
    $[22] = t9;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  const t11 = `relative inline-flex h-2 w-2 rounded-full transition-colors ${isListening ? "bg-green-500" : "bg-zinc-400 dark:bg-zinc-500"}`;
  let t12;
  if ($[24] !== t11) {
    t12 = <span className={t11} />;
    $[24] = t11;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  let t13;
  if ($[26] !== t10 || $[27] !== t12) {
    t13 = <span className="relative mt-2 inline-flex h-2 w-2 flex-shrink-0" aria-hidden="true">{t10}{t12}</span>;
    $[26] = t10;
    $[27] = t12;
    $[28] = t13;
  } else {
    t13 = $[28];
  }
  let t14;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <div className="min-w-0 flex-1 space-y-1"><p className="body-semibold">Listen for live events</p><p className="body-sm pb-2">Start listening, then trigger the real event (send a webhook, post a Slack message, etc.). The workflow will run automatically when the event is detected.</p></div>;
    $[29] = t14;
  } else {
    t14 = $[29];
  }
  let t15;
  if ($[30] !== t13) {
    t15 = <div className="flex items-start gap-3">{t13}{t14}</div>;
    $[30] = t13;
    $[31] = t15;
  } else {
    t15 = $[31];
  }
  let t16;
  if ($[32] !== isListening || $[33] !== secondsRemaining) {
    t16 = isListening && <div className="flex items-center gap-3 rounded-xl border border-blue-200 bg-blue-50/60 p-4 dark:border-blue-800 dark:bg-blue-900/20"><PixelLoader size="md" color="blue" /><div><p className="text-sm font-medium text-blue-700 dark:text-blue-400">Listening for events...</p><p className="label-xs text-blue-500 dark:text-blue-500">{secondsRemaining}s remaining. Trigger your event now.</p></div></div>;
    $[32] = isListening;
    $[33] = secondsRemaining;
    $[34] = t16;
  } else {
    t16 = $[34];
  }
  let t17;
  if ($[35] !== displayError) {
    t17 = displayError && <p className="label-xs text-amber-600 dark:text-amber-400">{displayError}</p>;
    $[35] = displayError;
    $[36] = t17;
  } else {
    t17 = $[36];
  }
  let t18;
  if ($[37] !== t15 || $[38] !== t16 || $[39] !== t17) {
    t18 = <div className="space-y-4">{t15}{t16}{t17}</div>;
    $[37] = t15;
    $[38] = t16;
    $[39] = t17;
    $[40] = t18;
  } else {
    t18 = $[40];
  }
  return t18;
}